import React, { useState } from 'react';
import { connect } from 'react-redux';
import { ReduxState } from 'services/types';
import {
	RemoteData,
	OrderInfo,
	Languages,
	OrderProduct,
	SetProduct,
	Shopper,
	SetProductWithParentId,
	OrderObject,
	PurchaseTypes,
} from 'common/types';
import {
	Typography,
	Container,
	Button,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Icon,
	Checkbox,
	Badge,
	Snackbar,
	Dialog,
	DialogTitle,
	TextField,
	tableCellClasses,
} from '@mui/material';
import { Theme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import classNames from 'classnames';
import PersonIcon from 'common/assets/icons/icon-person-solid.svg';
import ClearIcon from '@mui/icons-material/Clear';
import { getSearchResults } from 'common/utils/searchFunctions';
import Spinner from 'common/components/Spinner';
import BarCodeReader from 'components/BarCodeReader';
import { getTranslation } from 'common/modules/translations';
import {
	getOrderProducts,
	getOrderShoppers,
	getOrderProductsAndShoppers,
} from 'common/api/frontend';
import { groupProductsBy } from 'common/utils/productUtils';
import {
	getUpdatedProductsForReturning,
	getUpdatedParentsOfSetProductsToBeReturned,
	getNewProductsAndShoppers,
	getUniqProductTypes,
} from 'common/utils/rentalUtils';
import { updateActiveRentalToDb } from 'services/utils';
import { getNextProductReturnTime } from 'common/utils/dateCalculations';
import AddIcon from '@mui/icons-material/Add';
import MinusIcon from '@mui/icons-material/Remove';
import { useTranslation } from 'services/localization/useTranslation';

import { Routes, push } from 'routing';

interface StateToProps {
	activeStoreRentals: RemoteData<OrderInfo[]>;
	lang: Languages;
}

interface highlightProduct {
	[productId: string]: boolean;
}

type InputEvent = React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>;

type Props = StateToProps & WithStyles<typeof styles>;

const ReturnView = (props: Props) => {
	const { t } = useTranslation();
	const { classes } = props;
	const [scannedBarCodes, setScannedBarCodes] = useState<string[]>([]);
	const [scannedProducts, setScannedProducts] = useState<OrderProduct[]>([]);
	const [scannedShoppers, setScannedShoppers] = useState<Shopper[]>([]);
	const [counter, setCounter] = useState<number>(0);
	const [relatedCounter, setRelatedCounter] = useState<number>(0);
	const [returnedCounter, setReturnedCounter] = useState<number>(0);
	const [returnCounter, setReturnCounter] = useState<number>(0);
	const [notFoundInActiveCounter, setNotFoundInActiveCounter] = useState<number>(0);
	const [highlightedProducts, setHighlightedProducts] = useState<highlightProduct[]>([]);
	const [includeChecked, setIncludeChecked] = useState<boolean>(true);
	const [showScannedScreen, setShowScannedScreen] = useState<boolean>(false);
	const [productDeleted, setProductDeleted] = useState<boolean>(false);
	const [openCancelDialog, setOpenCancelDialog] = useState<boolean>(false);
	const [latestBarCode, setLatestBarCode] = useState<string>('');
	const [mobileSummaryOpen, setMobileSummaryOpen] = useState<boolean>(false);

	const openCancel = () => {
		setOpenCancelDialog(true);
	};

	const cancelCancel = () => {
		setOpenCancelDialog(false);
	};

	const cancelReturn = () => {
		setScannedProducts([]);

		push(Routes.BookingsTab('active'));
	};

	const returnProducts = async () => {
		if (props.activeStoreRentals.kind !== 'FETCHED') {
			return;
		}
		const productsForGrouping = includeChecked
			? scannedProducts
			: getScannedProductsWithCodes(scannedProducts);
		const setProductsWithParentId = productsForGrouping
			.filter((p) => p.set)
			.map((p) =>
				p.setProducts.map((setP) => {
					const updatedSetP: SetProductWithParentId = {
						...setP,
						parentId: p.id,
					};
					return updatedSetP;
				}),
			)
			.flat();
		const updatedSetProducts = getUpdatedParentsOfSetProductsToBeReturned(
			setProductsWithParentId,
			productsForGrouping,
		);
		const productIdsToReturn = productsForGrouping.filter((p) => !p.set).map((p) => p.id);
		const updatedProducts = getUpdatedProductsForReturning(
			productIdsToReturn,
			updatedSetProducts.changes,
			productsForGrouping,
		);
		const rentalInfos = props.activeStoreRentals.data.filter((data) =>
			updatedProducts.full.some((product) => product.rentalId === data.id),
		);
		for (const rentalInfo of rentalInfos) {
			const allOrderProducts = await getOrderProducts(rentalInfo.id, rentalInfo.shopId);
			const updatedOrderProducts = allOrderProducts.map((prod) => {
				const updatedProduct = updatedProducts.full.find((p) => p.id === prod.id);
				return updatedProduct || prod;
			});
			const allOrderShoppers = await getOrderShoppers(rentalInfo.id, rentalInfo.shopId);
			const updatedOrderShoppers = allOrderShoppers.map((s) => {
				const shopperProducts = updatedOrderProducts.filter((p) => p.shopperId === s.id);
				let productsReturned = shopperProducts.every((p) => Boolean(p.endDateReturned));
				const shopper: Shopper = {
					...s,
					productsReturned,
				};
				return shopper;
			});
			const notReturnedProducts = updatedOrderProducts.filter((p) => !p.endDateReturned);
			const notReturnedShoppers = updatedOrderShoppers.filter((s) => !s.productsReturned);
			let updatedRentalInfo: OrderInfo = {
				...rentalInfo,
				includedProductTypes: getUniqProductTypes(updatedOrderProducts),
			};
			if (!notReturnedProducts.length && !notReturnedShoppers.length) {
				const endDate = new Date().toISOString();
				updatedRentalInfo = {
					...rentalInfo,
					activeState: 'RENT_ENDED',
					rentalState: 'COMPLETED',
					returnTimeNext: null,
					endDateReturned: endDate,
				};
			} else {
				const returnTimeNext = getNextProductReturnTime(notReturnedProducts);
				updatedRentalInfo = {
					...rentalInfo,
					returnTimeNext,
				};
			}
			const orderObject: OrderObject = {
				rentalInfo: updatedRentalInfo,
				shoppers: updatedOrderShoppers,
				products: updatedOrderProducts,
			};
			updateActiveRentalToDb(orderObject);
		}
		setScannedProducts([]);

		push(Routes.BookingsTab('active'));
	};

	const checkboxChanged = () => {
		updateReturnCounter(scannedProducts, !includeChecked);
		setIncludeChecked(!includeChecked);
	};

	const handleInputField = (e: InputEvent) => {
		setLatestBarCode(e.target.value.toUpperCase());
	};

	const handleEnterKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.keyCode === 13) {
			updateCounters(latestBarCode);
			setLatestBarCode('');
		}
	};

	const handleSnackBarClose = () => {
		setProductDeleted(false);
	};

	const handleOpenMobileSummary = () => {
		setMobileSummaryOpen(!mobileSummaryOpen);
	};

	const handleDelete = (deletedProduct: OrderProduct, deletedProductCode?: string) => {
		const updatedScannedProducts = includeChecked
			? scannedProducts.filter((p) => p.id !== deletedProduct.id)
			: getScannedProductsWithCodes(scannedProducts).filter((p) => p.id !== deletedProduct.id);
		updateReturnCounter(updatedScannedProducts, includeChecked);
		setScannedProducts(updatedScannedProducts);
		const deletedShopper = updatedScannedProducts.every(
			(p) => p.shopperId !== deletedProduct.shopperId,
		);
		const updatedScannedShoppers = deletedShopper
			? scannedShoppers.filter((shopper) => shopper.id !== deletedProduct.shopperId)
			: scannedShoppers;
		setScannedShoppers(updatedScannedShoppers);
		const updatedBarCodes = scannedBarCodes.filter((barCode) =>
			updatedScannedProducts.some(
				(updatedProduct) =>
					updatedProduct.productCode === barCode ||
					updatedProduct.setProducts.some((setProduct) => setProduct.productCode === barCode),
			),
		);
		setScannedBarCodes(updatedBarCodes);
		if (deletedProduct.set) {
			const setProductCodes = deletedProduct.setProducts.map(
				(setProduct) => setProduct.productCode,
			);
			if (
				scannedBarCodes.every((barCode) =>
					setProductCodes.every((setProductCode) => barCode !== setProductCode),
				)
			) {
				setRelatedCounter(relatedCounter - deletedProduct.setProducts.length);
			} else {
				setRelatedCounter(relatedCounter - (deletedProduct.setProducts.length - 1));
				setCounter(counter - 1);
			}
		} else if (scannedBarCodes.every((barCode) => barCode !== deletedProductCode)) {
			setRelatedCounter(relatedCounter - 1);
		} else {
			setCounter(counter - 1);
		}

		setProductDeleted(true);
		setInterval(() => {
			setProductDeleted(false);
		}, 1000);
	};

	const handleBarCodeRead = (latestBarCodeString: string) => {
		if (latestBarCodeString !== '') {
			updateCounters(latestBarCodeString);
		}
	};

	const updateCounters = async (latestBarCodeString: string) => {
		if (props.activeStoreRentals.kind !== 'FETCHED') {
			return;
		}
		const searchTarget = props.activeStoreRentals.data;
		const returnedRentalInfo = getSearchResults(latestBarCodeString, searchTarget, [
			'NAME',
			'PRODUCT_CODE',
			'ORDER_BARCODE',
		]);
		const foundNewProducts = returnedRentalInfo.filter((returnedInfo) =>
			returnedInfo.stockProductIds.every((stockProductId) =>
				scannedProducts.every((scannedProduct) => scannedProduct.id !== stockProductId),
			),
		);
		let updatedCounter = counter;
		if (foundNewProducts.length > 0) {
			setShowScannedScreen(true);
			setLatestBarCode('');
			setTimeout(function () {
				setShowScannedScreen(false);
			}, 1000);
			const returnObject = await updateProductsAndShoppers(
				foundNewProducts[0],
				latestBarCodeString,
			);
			const newProducts = returnObject.newProducts;
			const notReturnedProduct = returnObject.notReturnedProduct;
			if (!notReturnedProduct) {
				setReturnedCounter(returnedCounter + 1);
			}
			const highlightRow = newProducts.length === 0;
			const updatedRelatedCounter =
				notReturnedProduct &&
				highlightRow &&
				scannedBarCodes.every((barCode) => barCode !== latestBarCodeString)
					? relatedCounter - 1
					: updateRelatedCounter(newProducts);
			setRelatedCounter(updatedRelatedCounter);
			updatedCounter =
				notReturnedProduct &&
				(!highlightRow || scannedBarCodes.every((barCode) => barCode !== latestBarCodeString))
					? counter + 1
					: counter;
			setCounter(updatedCounter);
		} else {
			const updatedHighLightedProducts = scannedProducts.map((_) => {
				const highlightProduct: highlightProduct = {
					[latestBarCodeString]: false,
				};
				return highlightProduct;
			});
			setHighlightedProducts(updatedHighLightedProducts);
		}
		const updatedNotFoundInActiveCounter =
			returnedRentalInfo.length >= 1 ? notFoundInActiveCounter : notFoundInActiveCounter + 1;
		setNotFoundInActiveCounter(updatedNotFoundInActiveCounter);
		const updatedScannedBarCodes = [...scannedBarCodes, latestBarCodeString];
		setScannedBarCodes(updatedScannedBarCodes);
	};

	const updateProductsAndShoppers = async (rentalInfo: OrderInfo, latestBarCodeString: string) => {
		const ordersProductsAndShoppers = await getOrderProductsAndShoppers(rentalInfo);
		const productObjects = ordersProductsAndShoppers.productObjects;
		const shopperObjects = ordersProductsAndShoppers.shopperObjects;
		const newProductsAndShoppers = await getNewProductsAndShoppers(
			productObjects,
			shopperObjects,
			latestBarCodeString,
			scannedProducts,
		);
		const newShoppers = newProductsAndShoppers.shoppers.filter((shopper) =>
			scannedShoppers.every((scannedShopper) => scannedShopper.id !== shopper.id),
		);
		const newProducts = newProductsAndShoppers.products
			.filter((product) => product.endDateReturned === null)
			.filter((product) => product.purchaseType !== PurchaseTypes.sales)
			.filter((product) =>
				scannedProducts.every((scannedProduct) => scannedProduct.id !== product.id),
			);
		const notReturnedProduct =
			newProducts.length !== 0 &&
			newProducts.some((product) => {
				if (
					product.productCode === latestBarCodeString ||
					product.setProducts.some((setP) => setP.productCode === latestBarCodeString)
				) {
					return true;
				} else {
					return false;
				}
			});
		const products = notReturnedProduct
			? [...scannedProducts, ...newProducts]
			: [...scannedProducts];

		updateReturnCounter(products, includeChecked);

		const updatedShoppers = notReturnedProduct
			? [...scannedShoppers, ...newShoppers]
			: [...scannedShoppers];
		setScannedShoppers(updatedShoppers);
		setScannedProducts(products);
		const highlightRow = newProducts.length === 0;
		getHighligthedRow(highlightRow, products, latestBarCodeString);
		return notReturnedProduct === false
			? { newProducts: [], notReturnedProduct: notReturnedProduct }
			: { newProducts: newProducts, notReturnedProduct: notReturnedProduct };
	};

	const getHighligthedRow = (
		highlight: boolean,
		products: OrderProduct[],
		latestBarCodeString: string,
	) => {
		const updatedHighLightedProducts = products.map((_) => {
			const highlightProduct: highlightProduct = {
				[latestBarCodeString]: highlight,
			};
			return highlightProduct;
		});
		setHighlightedProducts(updatedHighLightedProducts);
		return highlight;
	};

	const updateReturnCounter = (products: OrderProduct[], checked: boolean) => {
		const returnProductsLength = checked
			? products
					.map((product) => {
						if (product.set) {
							return product.setProducts.length;
						} else {
							return 1;
						}
					})
					.reduce((a, b) => a + b, 0)
			: getScannedProductsWithCodes(products)
					.map((product) => {
						if (product.set) {
							return product.setProducts.length;
						} else {
							return 1;
						}
					})
					.reduce((a, b) => a + b, 0);
		setReturnCounter(returnProductsLength);
	};

	const updateRelatedCounter = (newProducts: OrderProduct[]) => {
		if (newProducts.length === 0) {
			return relatedCounter;
		}
		const setProducts = newProducts.filter((newProduct) => newProduct.setProducts.length > 0);
		const amountOfSetProducts = setProducts.length;
		const countRelatedSetProducts =
			setProducts.length > 0
				? setProducts
						.map((newProduct) => newProduct.setProducts.length)
						.reduce((a, b) => a + b, 0) - 1
				: 0;
		const countRelatedProducts =
			setProducts.length > 0 ? newProducts.length - amountOfSetProducts : newProducts.length - 1;
		const amoutOfNewRelatedProducts = countRelatedProducts + countRelatedSetProducts;
		return relatedCounter + amoutOfNewRelatedProducts;
	};

	const renderHeader = () => {
		const isMobile = window.matchMedia('screen and (max-width: 700px)').matches;
		return (
			<>
				{!isMobile ? (
					<div className={classNames(classes.flex, classes.alignCenter, classes.spacing4)}>
						<Typography variant="h5" className={classes.width50}>
							{t('returnView.scanProducts', 'Scan products')}
						</Typography>
						<div className={classes.width50}>
							<Badge badgeContent={returnCounter} className={classes.badgeRight}>
								<Button
									variant="contained"
									color="primary"
									className={classes.btn}
									onClick={returnProducts}
									disabled={returnCounter <= 0}
								>
									{t('returnView.returnProducts', 'Return products')}
								</Button>
							</Badge>
							<Button variant="text" color="primary" className={classes.btn} onClick={openCancel}>
								{t('common:actions.cancel')}
							</Button>
						</div>
					</div>
				) : (
					<>
						<Typography variant="h5" className={classNames(classes.width100, classes.marginTop24)}>
							{t('returnView.scanProducts', 'Scan products')}
						</Typography>
						<div className={classes.mobileBtnContainer}>
							<Button variant="text" color="primary" className={classes.btn} onClick={openCancel}>
								{t('common:actions.cancel')}
							</Button>
							<Badge badgeContent={returnCounter} className={classes.badgeRight} color="primary">
								<Button
									variant="contained"
									color="primary"
									className={classes.btn}
									onClick={returnProducts}
									disabled={returnCounter <= 0}
								>
									{t('returnView.returnProducts', 'Return products')}
								</Button>
							</Badge>
						</div>
					</>
				)}
			</>
		);
	};

	const renderReadyToReturnTable = () => {
		return (
			<>
				<Table
					className={classes.returnTable}
					sx={{
						[`& .${tableCellClasses.root}`]: {
							borderBottom: 'none',
						},
					}}
				>
					<TableHead>
						<TableRow>
							<TableCell className={classNames(classes.padding8, classes.width33)}>
								<Typography variant="h6" align="center">
									{t('returnView.totalToBeReturned', 'Total to be returned:')}
								</Typography>
							</TableCell>
							<TableCell className={classNames(classes.padding8, classes.width33)}>
								<Typography variant="h6" align="center">
									{t('returnView.notReturnedProductsFound', 'Unreturned products scanned:')}
								</Typography>
							</TableCell>
							<TableCell className={classNames(classes.padding8, classes.width33)}>
								<Typography variant="h6" align="center">
									{t('returnView.personsFound', 'Persons found:')}
								</Typography>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell>
								<Typography variant="h3" align="center" className={classes.bigNumber} sx={{ p: 2 }}>
									{returnCounter}
								</Typography>
							</TableCell>
							<TableCell>
								<Typography variant="h3" align="center" className={classes.bigNumber} sx={{ p: 2 }}>
									{counter}
								</Typography>
							</TableCell>
							<TableCell>
								<Typography variant="h3" align="center" className={classes.bigNumber} sx={{ p: 2 }}>
									{scannedShoppers.length}
								</Typography>
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>
								<Typography variant="body2"></Typography>
							</TableCell>
							<TableCell>
								{returnedCounter > 0 && (
									<Typography variant="body2" align="center" className={classes.alert}>
										{t('returnView.alreadyReturned', 'Already returned products:') +
											' ' +
											returnedCounter}
									</Typography>
								)}
								{notFoundInActiveCounter > 0 ? (
									<Typography variant="body2" align="center" className={classes.alert}>
										{t('returnView.notFoundInActiveRentals', 'Not found in active rentals: ') +
											' ' +
											notFoundInActiveCounter}
									</Typography>
								) : null}
							</TableCell>
							<TableCell>
								<Typography variant="body2" align="center">
									<Checkbox color="primary" checked={includeChecked} onChange={checkboxChanged} />
									{t('returnView.includeInReturn', `Include person's all rented products`)}
								</Typography>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</>
		);
	};

	const renderSummaryProductsTable = () => {
		const productsForGrouping = includeChecked
			? scannedProducts
			: getScannedProductsWithCodes(scannedProducts);
		const groupedByProducts = groupProductsBy(
			productsForGrouping,
			(product) => product.productApiId,
		);
		return (
			<Table>
				<TableBody>
					{Object.values(groupedByProducts).map((groupedProducts, index) => {
						if (groupedProducts) {
							const groupedProductLength = groupedProducts.length;
							let productName: string = '';
							if (!groupedProducts[0].set) {
								productName =
									groupedProducts[0] && getTranslation(groupedProducts[0].name, props.lang);
								const groupedProductsLength = groupedProducts.filter(
									(groupedProduct) => groupedProduct.endDateReturned === null,
								);
								if (groupedProductsLength.length > 0) {
									return (
										<TableRow key={index} className={classes.productRow}>
											<TableCell className={classes.borderBottom}>
												<Typography>{productName}</Typography>
											</TableCell>
											<TableCell className={classes.borderBottom}>
												<Typography>{groupedProductsLength.length + ' pcs'}</Typography>
											</TableCell>
										</TableRow>
									);
								}
							} else {
								const setProducts = groupedProducts[0].setProducts;
								return setProducts.map((setProduct) => {
									productName = getTranslation(setProduct.name, props.lang);
									return (
										<TableRow key={productName + index} className={classes.productRow}>
											<TableCell className={classes.borderBottom}>
												<Typography>{'Set: ' + productName}</Typography>
											</TableCell>
											<TableCell className={classes.borderBottom}>
												<Typography>{groupedProductLength + ' pcs'}</Typography>
											</TableCell>
										</TableRow>
									);
								});
							}
						}
						return null;
					})}
				</TableBody>
			</Table>
		);
	};

	const renderSummary = () => {
		const isMobile = window.matchMedia('screen and (max-width: 700px)').matches;
		return (
			<div className={classes.padding32}>
				{!isMobile ? (
					<>
						<Typography>{t('returnView.summary', 'Summary')}</Typography>
						{renderSummaryProductsTable()}
					</>
				) : (
					<>
						<div
							className={classNames(classes.flex, classes.spaceBetween)}
							onClick={handleOpenMobileSummary}
						>
							<Typography>{t('returnView.summary', 'Summary')}</Typography>
							{mobileSummaryOpen ? <MinusIcon /> : <AddIcon />}
						</div>
						{mobileSummaryOpen ? renderSummaryProductsTable() : null}
					</>
				)}
			</div>
		);
	};

	const renderRowProduct = (
		scannedProduct: OrderProduct,
		index: number,
		setProduct?: SetProduct,
		setIndex?: number,
	) => {
		const productName = setProduct
			? getTranslation(setProduct.name, props.lang)
			: getTranslation(scannedProduct.name, props.lang);
		const productCode = setProduct ? setProduct.productCode : scannedProduct.productCode;
		const highlighted = setProduct
			? setProduct.productCode &&
			  highlightedProducts[index] &&
			  highlightedProducts[index][setProduct.productCode] === true
			: scannedProduct.productCode &&
			  highlightedProducts[index] &&
			  highlightedProducts[index][scannedProduct.productCode] === true;
		return (
			<TableRow
				key={setIndex != null ? setIndex + index : index}
				className={
					highlighted
						? classNames(classes.productRow, classes.highlightedRow)
						: scannedBarCodes.some((barCode) => barCode === productCode)
						? classNames(classes.productRow, classes.mainProductRow)
						: classNames(classes.productRow, classes.relatedProductRow)
				}
			>
				{(setIndex != null ? setIndex === 0 : index === 0) ? (
					<TableCell className={classes.productRowPersonCell}>
						<Icon className={classes.iconSize}>
							<img
								className={
									highlighted ? classNames(classes.whiteText, classes.iconSize) : classes.iconSize
								}
								src={PersonIcon}
								alt={' '}
							/>
						</Icon>
					</TableCell>
				) : (
					<TableCell
						className={classNames(classes.whiteBackground, classes.productRowPersonCell)}
					></TableCell>
				)}
				<TableCell>
					<Typography className={highlighted ? classes.whiteText : undefined}>
						{setProduct ? 'Set: ' + productName : productName}
					</Typography>
				</TableCell>
				<TableCell>
					<Typography className={highlighted ? classes.whiteText : undefined}>
						{productCode}
					</Typography>
				</TableCell>
				<TableCell align={'right'}>
					{(!setProduct || setIndex === 0) && (
						<ClearIcon
							className={highlighted ? classes.whiteText : undefined}
							onClick={() => handleDelete(scannedProduct, productCode)}
						/>
					)}
				</TableCell>
			</TableRow>
		);
	};

	const getScannedProductsWithCodes = (scannedProducts: OrderProduct[]) => {
		const scannedProductsWithCodes = scannedProducts.filter((scannedProduct) =>
			scannedBarCodes.some((scannedBarCode) => scannedProduct.productCode === scannedBarCode),
		);
		const scannedSetProducts = scannedProducts.filter((scannedProduct) => scannedProduct.set);
		const scannedSetProductCodes = scannedSetProducts.filter((scannedSetProduct) => {
			const scannedSetProductCodeProducts = scannedSetProduct.setProducts.some((setProduct) =>
				scannedBarCodes.some((scannedBarCode) => scannedBarCode === setProduct.productCode),
			);
			return scannedSetProductCodeProducts;
		});
		const returnArray = scannedProductsWithCodes.concat(scannedSetProductCodes);
		return returnArray;
	};

	const renderProductRow = () => {
		const productsForGrouping = includeChecked
			? scannedProducts
			: getScannedProductsWithCodes(scannedProducts);
		const groupedByShopper = groupProductsBy(productsForGrouping, (product) => product.shopperId);
		return Object.values(groupedByShopper).map((productsByShopper) => {
			return productsByShopper.map((productByShopper, index) => {
				if (!productByShopper.set) {
					return renderRowProduct(productByShopper, index);
				} else {
					const setProducts = productByShopper.setProducts;

					return setProducts.map((setProduct, indexSetProduct) => {
						return renderRowProduct(productByShopper, index, setProduct, indexSetProduct);
					});
				}
			});
		});
	};

	const renderInputField = () => {
		const isMobile = window.matchMedia('screen and (max-width: 700px)').matches;
		return (
			<TextField
				label={t('returnView.searchCode', 'Search by product code')}
				name="barcode"
				value={latestBarCode}
				onChange={(e) => handleInputField(e)}
				className={!isMobile ? classes.textField : classes.width100}
				onKeyDown={(e) => handleEnterKey(e)}
			/>
		);
	};

	const renderProductsScannedTable = () => {
		return (
			<Table>
				<TableBody>{renderProductRow()}</TableBody>
			</Table>
		);
	};

	const renderProductsScanned = () => {
		return (
			<div className={classes.padding32}>
				<Typography sx={{ mb: 1 }}>
					{t(
						'returnView.productsScanned',
						'Products scanned ( + products rented by the same person):',
					)}
				</Typography>
				{renderProductsScannedTable()}
			</div>
		);
	};

	const renderSnackbar = () => {
		return (
			<Snackbar
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				classes={{
					root: classes.snackRoot,
				}}
				open={productDeleted}
				ContentProps={{
					'aria-describedby': 'message-id',
					classes: {
						root: classes.contentRoot,
						message: classes.contentMessage,
						action: classes.margin0,
					},
				}}
				message={
					<span id="message-id">{t('returnView.productDeleted', 'Product was deleted')}</span>
				}
				action={<ClearIcon className={classes.whiteText} onClick={() => handleSnackBarClose()} />}
			/>
		);
	};

	const renderDialog = () => {
		return (
			<Dialog
				onClose={cancelCancel}
				open={openCancelDialog}
				classes={{
					paper: classes.paper,
				}}
			>
				<DialogTitle>{t('returnView.areYouSureCancel', 'Cancel the return?')}</DialogTitle>
				<Typography variant="body2">
					{t(
						'returnView.thisActionCanNotUndone',
						'This cannot be undone. All scanned products will stay unreturned.',
					)}
				</Typography>
				<div className={classes.buttonContainer}>
					<Button
						variant="outlined"
						color="primary"
						classes={{ root: classNames(classes.btn, classes.extraSpacing) }}
						onClick={cancelCancel}
					>
						{t('common:actions.resume', 'Resume')}
					</Button>
					<Button
						variant="contained"
						color="primary"
						className={classes.btn}
						onClick={cancelReturn}
					>
						{t('common:actions.cancelReturn', 'Yes, cancel')}
					</Button>
				</div>
			</Dialog>
		);
	};

	const fullPage = () => {
		const isMobile = window.matchMedia('screen and (max-width: 700px)').matches;
		return (
			<>
				<div className={showScannedScreen ? classes.greenScreen : undefined}></div>
				<Container>
					{renderHeader()}
					{renderReadyToReturnTable()}
					{!isMobile ? (
						<>
							{renderInputField()}
							<div className={classNames(classes.flex, classes.paddingBottom)}>
								<div className={classNames(classes.width50, classes.borderLight)}>
									{renderSummary()}
								</div>
								<div className={classes.width100}>{renderProductsScanned()}</div>
							</div>
						</>
					) : (
						<>
							{renderInputField()}
							<div className={classNames(classes.width100)}>{renderSummary()}</div>
							<div className={classes.width100}>{renderProductsScanned()}</div>
						</>
					)}
					{renderSnackbar()}
					{renderDialog()}
				</Container>
			</>
		);
	};

	return (
		<>
			{props.activeStoreRentals.kind === 'FETCHED' ? (
				<BarCodeReader barCodeRead={(p) => handleBarCodeRead(p)}>{fullPage()}</BarCodeReader>
			) : (
				<Container>
					<Spinner />
				</Container>
			)}
		</>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		btn: {
			paddingTop: 12,
			paddingBottom: 12,
			borderRadius: 50,
			float: 'right',
		},
		flex: {
			display: 'flex',
			flex: '1 0 0',
		},
		returnTable: {
			marginBottom: theme.spacing(4),
		},
		borderLight: {
			borderRight: `1px solid ${theme.palette.divider}`,
		},
		padding8: {
			padding: theme.spacing(1),
		},
		borderBottom: {
			borderBottom: `1px solid ${theme.palette.divider}`,
		},
		padding32: {
			padding: theme.spacing(4),
		},
		paddingBottom: {
			paddingBottom: theme.spacing(3),
		},
		spacing4: {
			margin: '32px 0',
		},
		bigNumber: {
			fontSize: '100px',
			lineHeight: '0.5',
		},
		iconSize: {
			width: theme.spacing(3),
			height: theme.spacing(3),
		},
		productRow: {
			maxHeight: theme.spacing(6),
		},
		alert: {
			color: theme.palette.error.main,
		},
		mainProductRow: {
			backgroundColor: theme.palette.background.secondary,
			borderTop: `${theme.spacing(1)} solid ${theme.palette.border.paper}`,
		},
		relatedProductRow: {
			backgroundColor: theme.palette.background.secondary,
			borderTop: `2px solid ${theme.palette.divider}`,
		},
		highlightedRow: {
			backgroundColor: theme.palette.background.secondary,
		},
		whiteBackground: {
			backgroundColor: theme.palette.background.default,
			borderTop: `2px solid ${theme.palette.border.paper}`,
		},
		productRowPersonCell: {
			maxWidth: '50px',
			padding: '0 8px',
			textAlign: 'center',
		},
		alignCenter: {
			alignItems: 'center',
		},
		width33: {
			width: '33.3333%',
		},
		width50: {
			width: '50%',
		},
		width100: {
			width: '100%',
		},
		greenScreen: {
			backgroundColor: '#00FF8D',
			opacity: 0.87,
			width: '100%',
			height: '100%',
			position: 'absolute',
			zIndex: 2,
		},
		whiteText: {
			color: theme.palette.common.white,
		},
		badgeRight: {
			float: 'right',
		},
		'@global': {
			'.MuiBadge-badge': {
				backgroundColor: theme.palette.success.light,
				height: 24,
				width: 24,
				top: theme.spacing(1),
				right: theme.spacing(1),
				borderRadius: 12,
				color: theme.palette.common.white,
			},
		},
		snackRoot: {
			width: '97%',
			bottom: 0,
		},
		contentRoot: {
			backgroundColor: theme.palette.primary.main,
			maxWidth: 'none',
			flexGrow: 1,
		},
		contentMessage: {
			margin: '0 auto',
		},
		margin0: {
			margin: 0,
		},
		paper: {
			padding: theme.spacing(2),
		},
		buttonContainer: {
			marginTop: theme.spacing(3),
			display: 'inline-flex',
			justifyContent: 'center',
		},
		redBtn: {
			color: theme.palette.error.main,
			border: `1px solid ${theme.palette.error.main}`,
			marginRight: theme.spacing(1),
		},
		extraSpacing: {
			marginRight: '8px',
		},
		textField: {
			marginLeft: '35%',
			width: '65%',
		},
		mobileBtnContainer: {
			height: 50,
			width: '100%',
			marginTop: theme.spacing(3),
			marginBottom: theme.spacing(3),
			justifyContent: 'center',
			display: 'inline-flex',
		},
		marginTop24: {
			marginTop: theme.spacing(3),
		},
		spaceBetween: {
			justifyContent: 'space-between',
		},
	});

const mapStateToProps = ({ storeRentals, lang }: ReduxState): StateToProps => {
	const { activeStoreRentals } = storeRentals;
	return {
		activeStoreRentals,
		lang,
	};
};

export default withStyles(styles)(connect(mapStateToProps, {})(ReturnView));
